import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetMatter, NewMttr, updateMatter, ReorderMatter } from '../../Api';

import {
    Container,
    List,
    Emp,
    EmpMain,
    MatterIcon,
    Span,
    Search,
    DivNewEmp,
    DivAddEmp,
    FormSearch,
    Btt02,
    AddBtn,
    EditBtn,
    AreaEmp,
    InputEmp,
    EditContainer,
    BoxButtonEdit,
    LoadingSpinnerContainer,
    ReorderBtn,
    DragHandle,
    OrderPos,
    ReorderHint,
} from './style';

import LoadingSpinner from '../../components/Loading';

/* Educação Física é uma matéria predefinida pelo sistema: nunca é editada
   nem apagada, e o resto do sistema a identifica PELO NOME. Por isso o nome
   canônico fica numa constante única e toda comparação é normalizada. */
const EF_NAME = 'EDUCAÇÃO FÍSICA';
const normalize = (s = '') => s.trim().toUpperCase();
const isEF = (m) => normalize(m?.name) === EF_NAME;

/* faixa/realce colorido por item (apenas visual) */
const ACCENTS = ['#A5D6A7', '#90CAF9', '#FFCC80', '#CE93D8', '#80DEEA', '#FFAB91', '#DCE775', '#B0BEC5'];

const Matter = () => {
    const navigate = useNavigate();

    const [matter, setMatter] = useState([]);
    const [name, setname] = useState('');

    const [EditingMatter, setEditingMatter] = useState(null);
    const [EditingName, setEditingName] = useState('');
    const [/*nameMatter*/, setNamesMatter] = useState('');

    const [idSchool, setIdSchool] = useState([]);
    const [busca, setBusca] = useState("");
    const [positionAtSchool, setPositionAtSchool] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);

    // ── modo "editar ordem" ──
    const [isReordering, setIsReordering] = useState(false);
    const [orderItems, setOrderItems] = useState([]); // matérias na ordem editável (sem a EF, que fica fixa no topo)
    const [savingOrder, setSavingOrder] = useState(false);
    const [overIndex, setOverIndex] = useState(null);
    const dragIndex = useRef(null);

    const hasCreated = useRef(false); // ← flag de controle (evita criação múltipla na sessão)

    useEffect(() => {
        (async () => {
            setLoading(true);
            const id_school = sessionStorage.getItem("id-school");
            setIdSchool(JSON.parse(id_school))
            const res = await GetMatter(JSON.parse(id_school));
            const position = localStorage.getItem('position_at_school');
            setPositionAtSchool(position);
            setMatter(res.data.data);
            setname(EF_NAME)
            setLoading(false);
        })();
    }, []);

    // ── Garante a Educação Física predefinida ──
    // Regra: a EF deve existir exatamente UMA vez. Criamos automaticamente
    // somente se ela NÃO estiver presente na lista (comparação por nome
    // normalizado), e nunca uma segunda vez. Isso é mais seguro que checar
    // matter.length, pois não recria/duplica quando já há matérias.
    useEffect(() => {
        const createEF = async () => {
            if (hasCreated.current) return; // impede execução múltipla
            hasCreated.current = true;

            const resNewMatter = await NewMttr(idSchool, EF_NAME);
            if (resNewMatter.status === 200) {
                setIsSubmitting(true);
                window.location.reload();
            } else {
                setIsSubmitting(false);
                hasCreated.current = false; // falhou: permite nova tentativa
            }
        };

        // só dispara quando já temos escola, não estamos enviando, e a EF
        // realmente não existe na lista carregada do backend.
        const efExists = matter.some(isEF);
        if (idSchool && name && !isSubmitting && !efExists) {
            createEF();
        }
    }, [matter, name, idSchool, isSubmitting]);

    // mantém a ordem definida pelo backend (campo `order`), apenas garantindo
    // que a Educação Física fique sempre no final (matéria predefinida do sistema)
    const sortedMatter = [...matter].sort((a, b) => {
        if (isEF(a)) return 1;
        if (isEF(b)) return -1;
        return 0;
    });

    const filteredMatter = sortedMatter.filter((val) => {
        if (!busca) return true;
        return val.name.toUpperCase().includes(busca.toUpperCase());
    });

    const NewMatter = async () => {
        navigate('/new/matter');
    };

    const startEditing = (m) => {
        // trava de segurança: EF nunca é editável, mesmo que algo chame isto
        if (isEF(m)) return;
        setEditingMatter(m._id);
        setEditingName(m.name);
        setNamesMatter(m)
    };

    const saveEdit = async () => {
        setLoading(true)
        if (!EditingName.trim()) {
            alert("O nome do componente curricular não pode estar vazio.");
            setLoading(false)
            return;
        }
        // impede renomear qualquer matéria PARA "Educação Física" (nome reservado)
        if (normalize(EditingName) === EF_NAME) {
            alert('"Educação Física" é uma matéria predefinida do sistema e não pode ser duplicada.');
            setLoading(false)
            return;
        }

        await updateMatter(EditingMatter, EditingName.toLocaleUpperCase())
        window.location.reload()
        //setLoading(false)
    };

    // ── abrir o modo de reordenação ──
    // a EF não entra na lista arrastável (fica fixa no topo)
    const openReorder = () => {
        setOrderItems(matter.filter((m) => !isEF(m)));
        setIsReordering(true);
    };

    const closeReorder = () => {
        setIsReordering(false);
        setOverIndex(null);
        dragIndex.current = null;
    };

    const handleDragStart = (index) => { dragIndex.current = index; };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (index !== overIndex) setOverIndex(index);
    };

    const handleDrop = (index) => {
        const from = dragIndex.current;
        if (from === null || from === index) {
            dragIndex.current = null;
            setOverIndex(null);
            return;
        }
        setOrderItems((prev) => {
            const updated = [...prev];
            const [moved] = updated.splice(from, 1);
            updated.splice(index, 0, moved);
            return updated;
        });
        dragIndex.current = null;
        setOverIndex(null);
    };

    const handleDragEnd = () => {
        dragIndex.current = null;
        setOverIndex(null);
    };

    const saveOrder = async () => {
        try {
            setSavingOrder(true);
            // EF sempre por último, após as demais na ordem escolhida
            const ef = matter.find(isEF);
            const finalList = ef ? [...orderItems, ef] : [...orderItems];
            const orderedIds = finalList.map((m) => m._id);

            await ReorderMatter(orderedIds);

            // reflete localmente sem precisar recarregar a página
            setMatter(finalList);
            closeReorder();
        } catch (err) {
            console.error(err);
            alert('Erro ao salvar a ordem das disciplinas.');
        } finally {
            setSavingOrder(false);
        }
    };

    const canManage = positionAtSchool !== 'PROFESSOR';

    if (loading) {
        return (
            <Container>
                <LoadingSpinnerContainer>
                    <LoadingSpinner />
                </LoadingSpinnerContainer>
            </Container>
        );
    }

    return (
        <Container>
            <h2>Componentes Curriculares</h2>

            <Search>
                <FormSearch onSubmit={(e) => e.preventDefault()}>
                    <label>Buscar</label>
                    <AreaEmp>
                        <InputEmp
                            type="text"
                            placeholder="Buscar por nome"
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                        />
                    </AreaEmp>
                </FormSearch>
            </Search>

            <List>
                {canManage && (
                    <DivAddEmp>
                        <DivNewEmp>
                            <AddBtn onClick={NewMatter}>Cadastrar</AddBtn>
                        </DivNewEmp>
                        {!isReordering && matter.filter((m) => !isEF(m)).length > 1 && (
                            <ReorderBtn onClick={openReorder}>Editar ordem</ReorderBtn>
                        )}
                    </DivAddEmp>
                )}

                {isReordering ? (
                    <>
                        <ReorderHint>
                            Arraste as disciplinas para definir a ordem. A Educação Física permanece fixa no final.
                        </ReorderHint>

                        {orderItems.map((m, index) => {
                            const accent = ACCENTS[index % ACCENTS.length];
                            return (
                                <Emp
                                    key={m._id}
                                    draggable
                                    onDragStart={() => handleDragStart(index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={() => handleDrop(index)}
                                    onDragEnd={handleDragEnd}
                                    style={{
                                        '--accent': accent,
                                        cursor: 'grab',
                                        borderColor: overIndex === index ? '#e07a5f' : undefined,
                                        borderWidth: overIndex === index ? 2 : undefined,
                                    }}
                                >
                                    <EmpMain>
                                        <DragHandle>⠿</DragHandle>
                                        <OrderPos>{index + 1}º</OrderPos>
                                        <Span>{m.name}</Span>
                                    </EmpMain>
                                </Emp>
                            );
                        })}

                        {/* EF fixa, não arrastável, sempre no final */}
                        {matter.filter(isEF).map((m) => (
                            <Emp key={m._id} style={{ '--accent': '#0a5a73', opacity: 0.85 }}>
                                <EmpMain>
                                    <MatterIcon style={{ color: '#fff' }}>🏃</MatterIcon>
                                    <Span>{m.name}</Span>
                                </EmpMain>
                                <OrderPos>fixa</OrderPos>
                            </Emp>
                        ))}

                        <BoxButtonEdit style={{ marginTop: 10 }}>
                            <Btt02 onClick={saveOrder} disabled={savingOrder}>
                                {savingOrder ? 'Salvando...' : 'Salvar ordem'}
                            </Btt02>
                            <Btt02 onClick={closeReorder} disabled={savingOrder}>
                                Cancelar
                            </Btt02>
                        </BoxButtonEdit>
                    </>
                ) : filteredMatter.length > 0 ? (
                    filteredMatter.map((m, index) => {
                        const accent = isEF(m) ? '#0a5a73' : ACCENTS[index % ACCENTS.length];
                        const protectedEF = isEF(m);
                        return (
                            <Emp key={m._id} style={{ '--accent': accent }}>
                                <EmpMain>
                                    <MatterIcon style={protectedEF ? { color: '#fff' } : undefined}>
                                        {protectedEF ? '🏃' : (m.name?.[0] || '?')}
                                    </MatterIcon>
                                    <Span>{m.name}</Span>
                                </EmpMain>
                                {canManage && !protectedEF && (
                                    <EditBtn onClick={() => startEditing(m)}>Editar</EditBtn>
                                )}
                            </Emp>
                        );
                    })
                ) : (
                    <p>
                        {busca
                            ? 'Nenhum componente curricular encontrado para essa busca.'
                            : 'Nenhum componente curricular cadastrado.'}
                    </p>
                )}
            </List>

            {EditingMatter && (
                <EditContainer onClick={() => setEditingMatter(null)}>
                    <div onClick={(e) => e.stopPropagation()}>
                        <h3>Editando Componente Curricular</h3>
                        <span>Nome da Disciplina</span>
                        <input
                            placeholder="Digite o nome da disciplina"
                            value={EditingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            autoFocus
                        />
                        <BoxButtonEdit>
                            <Btt02 onClick={saveEdit}>Salvar</Btt02>
                            <Btt02 onClick={() => setEditingMatter(null)}>Cancelar</Btt02>
                        </BoxButtonEdit>
                    </div>
                </EditContainer>
            )}
        </Container>
    );
};

export default Matter;